import React, { useCallback, useMemo, useRef } from "react";

import { Select, SelectOption } from "@labxchange/ui-components/lib/Select";

import { TableCellProps, CellChangeParams } from "../types";
import { useEditableCell } from "../hooks/useEditableCell";

export interface DropdownCellProps
  extends Pick<
    TableCellProps,
    | "cellId"
    | "rowIndex"
    | "colIndex"
    | "placeholder"
    | "options"
    | "value"
    | "disabled"
    | "showFeedback"
    | "isCorrect"
    | "feedback"
    | "unique"
    | "colspan"
  > {
  hint?: string;
  isPrediction?: boolean;
  isSubmitted?: boolean;
  onChange?: (params: Omit<CellChangeParams, "componentID">) => void;
}

export const DropdownCell: React.FC<DropdownCellProps> = ({
  cellId,
  rowIndex,
  colIndex,
  placeholder,
  options = [],
  value,
  disabled,
  showFeedback,
  isCorrect,
  feedback,
  unique,
  colspan,
  hint,
  isPrediction,
  isSubmitted,
  onChange,
}) => {
  const {
    cellClass,
    state,
    translatedFeedback,
    translatedHint,
    shouldShowFeedback,
  } = useEditableCell({
    value,
    showFeedback,
    isCorrect,
    feedback,
    hint,
    isPrediction,
  });

  const selectOptions: SelectOption[] = useMemo(
    () =>
      options.map((option) => ({
        value: option,
        label: option,
      })),
    [options]
  );

  const handleChange = useCallback(
    (newValue: string) => {
      onChange?.({
        rowId: rowIndex,
        colId: colIndex,
        value: newValue === "" ? null : newValue,
        unique,
      });
    },
    [onChange, rowIndex, colIndex, unique]
  );

  const cellRef = useRef<HTMLTableCellElement>(null);
  const isPredictionSubmitted = isPrediction && isSubmitted;

  const handleCellClick = useCallback((e: React.MouseEvent) => {
    const select = cellRef.current?.querySelector("select");
    if (select && e.target !== select) {
      select.focus();
      select.click();
    }
  }, []);

  return (
    <td
      ref={cellRef}
      key={cellId}
      className={cellClass}
      colSpan={colspan ? Number(colspan) : undefined}
      onClick={handleCellClick}
      style={{ cursor: "pointer" }}
    >
      <Select
        id={cellId}
        options={selectOptions}
        value={typeof value === "string" ? value : (value?.toString() ?? "")}
        placeholder={placeholder}
        onChange={handleChange}
        disabled={disabled || isPredictionSubmitted}
        ariaLabel={placeholder}
        variant="borderless"
        state={state}
        feedback={translatedFeedback}
        showFeedback={shouldShowFeedback}
        hint={translatedHint}
        hintIconSize={24}
      />
    </td>
  );
};
